import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";

const NavButton = ({ side, onClick, testId }) => (
  <button type="button" onClick={onClick} aria-label={side === "left" ? "Previous photo" : "Next photo"} data-testid={testId}
    className={`absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 sm:flex ${side === "left" ? "left-4" : "right-4"}`}>
    {side === "left" ? <ChevronLeft className="h-7 w-7" /> : <ChevronRight className="h-7 w-7" />}
  </button>
);

export const PhotoViewer = ({ photos, urls, index, onIndex, onClose }) => {
  const touch = useRef(null);
  const [dx, setDx] = useState(0);
  const count = photos.length;
  const go = (step) => onIndex((index + step + count) % count);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  });

  const onTouchEnd = () => {
    if (count > 1 && Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touch.current = null;
    setDx(0);
  };

  const photo = photos[index];
  const url = urls[photo.id];
  return createPortal(
    <div className="fixed inset-0 z-[100] flex select-none flex-col bg-black/95" role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="photo-viewer">
      <div className="flex items-center justify-between px-4 py-3 text-sm text-white/80">
        <span data-testid="photo-viewer-counter">{index + 1} / {count}</span>
        <button type="button" onClick={onClose} aria-label="Close photo viewer" data-testid="photo-viewer-close"
          className="flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/15"><X className="h-6 w-6" /></button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 pb-6"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchMove={(e) => touch.current !== null && setDx(e.touches[0].clientX - touch.current)}
        onTouchEnd={onTouchEnd} onClick={(e) => e.target === e.currentTarget && onClose()}>
        {url && url !== "error" ? (
          <img src={url} alt={photo.original_filename} draggable={false} data-testid="photo-viewer-image"
            style={{ transform: `translateX(${dx}px)`, transition: dx ? "none" : "transform 200ms ease-out" }}
            className="max-h-full max-w-full object-contain" />
        ) : url === "error" ? <p className="text-white/70">This photo couldn't be loaded.</p>
          : <Loader2 className="h-8 w-8 animate-spin text-white/60" />}
        {count > 1 && <NavButton side="left" onClick={() => go(-1)} testId="photo-viewer-prev" />}
        {count > 1 && <NavButton side="right" onClick={() => go(1)} testId="photo-viewer-next" />}
      </div>
      {count > 1 && <p className="pb-4 text-center text-xs text-white/50 sm:hidden">Swipe to see more photos</p>}
    </div>,
    document.body,
  );
};
